import type { Schemas } from "@platform/contracts/client";
import {
  Button,
  EmptyState,
  formatMoney,
  Input,
  Note,
  PageHeader,
  Panel,
  SearchInput,
  Select,
  StatusBadge,
  statusTone,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@platform/ui";
import { ErrorText, errorKey, localName, useSession } from "@platform/ui/features";
import { Package } from "lucide-react";
import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useTranslations } from "use-intl";
import { ConsoleCrumb } from "../components/crumb";
import { formatDateTime, locale } from "../lib/i18n";

type Summary = Schemas["OpsOrderSummary"];
type View = Schemas["OpsOrderView"];

const money = (amount: Schemas["Money"]) => formatMoney(amount, locale);

/**
 * Support's order lookup (ADM-5, card 080): by order number or a bank reference a customer quotes, or an enterprise's
 * orders when reached from its page (`?enterpriseId=`).
 */
export function OrdersPage() {
  const t = useTranslations();
  const { session } = useSession();
  const [params] = useSearchParams();
  const enterpriseId = params.get("enterpriseId") ?? undefined;
  // The console bar's look-up arrives as `?q=` (card 110).
  const asked = params.get("q") ?? undefined;
  const [q, setQ] = useState(asked ?? "");
  const [items, setItems] = useState<readonly Summary[] | undefined>();
  const [error, setError] = useState<string | undefined>();

  const search = useCallback(
    async (text: string) => {
      const { data, error: problem } = await session.api.GET("/v1/ops/orders", {
        params: {
          query: {
            ...(text.trim() === "" ? {} : { q: text.trim() }),
            ...(enterpriseId === undefined ? {} : { enterpriseId }),
          },
        },
      });
      if (data === undefined) return setError(t(errorKey(problem)));
      setError(undefined);
      setItems(data.items);
    },
    [session, enterpriseId, t],
  );

  useEffect(() => {
    if (asked !== undefined) setQ(asked);
    if (enterpriseId !== undefined || asked !== undefined) void search(asked ?? "");
  }, [enterpriseId, asked, search]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void search(q);
  };

  return (
    <section aria-labelledby="orders-title" className="flex flex-col gap-5">
      <PageHeader
        id="orders-title"
        title={t("admin.orders.title")}
        description={t(enterpriseId === undefined ? "admin.orders.intro" : "admin.orders.ofEnterprise")}
      />
      <form className="flex gap-2" onSubmit={submit}>
        <SearchInput
          aria-label={t("admin.orders.search")}
          placeholder={t("admin.orders.search")}
          className="w-full max-w-md border-border bg-surface"
          value={q}
          maxLength={100}
          onChange={(event) => setQ(event.target.value)}
        />
        <Button type="submit" variant="secondary">
          {t("admin.orders.find")}
        </Button>
      </form>
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      {items !== undefined && (
        <div className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
          {items.length === 0 ? (
            <EmptyState icon={Package} title={t("admin.orders.none")} />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t("admin.orders.number")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.orders.buyer")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.orders.shop")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.orders.status")}</TableHeaderCell>
                  <TableHeaderCell numeric>{t("admin.orders.total")}</TableHeaderCell>
                  <TableHeaderCell>{t("admin.orders.created")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Link
                        to={`/orders/${item.id}`}
                        className="font-medium text-ink tabular-nums hover:text-brand-600"
                      >
                        {item.number}
                      </Link>
                    </TableCell>
                    <TableCell>{localName(item.buyer.name, locale)}</TableCell>
                    <TableCell>{localName(item.shop.name, locale)}</TableCell>
                    <TableCell>
                      <StatusBadge
                        label={t(`trade.orderStatus.${item.status}`)}
                        tone={statusTone("order", item.status)}
                      />
                    </TableCell>
                    <TableCell numeric>{money(item.total)}</TableCell>
                    <TableCell className="tabular-nums">{formatDateTime(item.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </section>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Panel title={title} className="text-small">
      {children}
    </Panel>
  );
}

/** A small table of rows, each a list of cells; the header names the columns. */
function Rows({ head, rows }: { head: readonly string[]; rows: readonly (readonly ReactNode[])[] }) {
  return (
    <div className="-mx-5 border-b border-border-subtle first:-mt-4 last:-mb-4 last:border-b-0">
      <Table>
        <TableHead>
          <TableRow>
            {head.map((name) => (
              <TableHeaderCell key={name}>{name}</TableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells, row) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows of a read-only view, never reordered
            <TableRow key={row} className="align-top">
              {cells.map((cell, column) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed columns
                <TableCell key={column}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * One order for support (ADM-5, card 080), read-only: as both parties see it, with its transfers, shipments, change
 * orders, order sheets, ledger journals, and its conversation's members. Nothing here changes it; every view is
 * recorded on the operator's audit trail.
 */
export function OrderPage() {
  const t = useTranslations();
  const { orderId = "" } = useParams();
  const { session, state } = useSession();
  // Message content only on a reason and a 30-minute grant (card 081), for operators who may ask for one.
  const readsContent =
    state.status === "signed_in" && state.claims.perm.includes("ops:support.content-access");
  // Reviews that break the content rules are hidden by operators who keep the catalog (card 092).
  const moderates = state.status === "signed_in" && state.claims.perm.includes("ops:catalog.manage");
  const [view, setView] = useState<View | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    void session.api
      .GET("/v1/ops/orders/{orderId}", { params: { path: { orderId } } })
      .then(({ data, error: problem }) =>
        data === undefined ? setError(t(errorKey(problem))) : setView(data),
      );
  }, [session, orderId, t]);

  if (view === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  const buyer = localName(view.buyer.name, locale);
  const shop = localName(view.shop.name, locale);
  const partyOf = (enterpriseId: string | null) =>
    enterpriseId === null
      ? t("admin.orders.platform")
      : enterpriseId === view.buyer.enterpriseId
        ? buyer
        : shop;
  const none = <p className="text-ink-muted">{t("admin.orders.nothing")}</p>;

  return (
    <section aria-labelledby="order-title" className="flex flex-col gap-5">
      <PageHeader
        id="order-title"
        breadcrumb={<ConsoleCrumb to="/orders" back={t("admin.orders.title")} current={view.number} />}
        title={view.number}
        marks={
          <>
            <StatusBadge
              label={t(`trade.orderStatus.${view.status}`)}
              tone={statusTone("order", view.status)}
            />
            <StatusBadge
              label={t(`trade.fulfilment.${view.fulfilmentStatus}`)}
              tone={statusTone("fulfilment", view.fulfilmentStatus)}
            />
          </>
        }
      />
      <Note>{t("admin.orders.readOnly")}</Note>

      <Section title={t("admin.orders.parties")}>
        <p>{t("admin.orders.buyerIs", { name: buyer })}</p>
        <p>{t("admin.orders.shopIs", { name: shop })}</p>
      </Section>

      <Section title={t("admin.orders.terms")}>
        <p>{`${view.incoterm} ${view.namedPlace}`}</p>
        <p>
          {t("admin.orders.currencies", {
            settlement: view.settlementCurrency,
            listing: view.listingCurrency,
          })}
        </p>
        {view.fxSnapshot !== null && (
          <p>
            {t("admin.orders.rate", {
              base: view.fxSnapshot.base,
              quote: view.fxSnapshot.quote,
              rate: view.fxSnapshot.rate,
              when: formatDateTime(view.fxSnapshot.capturedAt),
            })}
          </p>
        )}
        <p>{t(view.protection === "none" ? "admin.orders.noEscrow" : "admin.orders.escrow")}</p>
        <p>{t("admin.orders.createdAt", { when: formatDateTime(view.createdAt) })}</p>
      </Section>

      <Section title={t("admin.orders.lines")}>
        <Rows
          head={[
            t("admin.orders.product"),
            t("admin.orders.quantity"),
            t("admin.orders.shipped"),
            t("admin.orders.total"),
          ]}
          rows={view.lines.map((line) => [
            `${localName(line.productName, locale)}${line.skuCode === null ? "" : ` · ${line.skuCode}`}`,
            `${line.quantity} ${line.unit}`,
            `${line.shippedQuantity} ${line.unit}`,
            money(line.lineTotal),
          ])}
        />
        <p className="text-right font-medium">{t("admin.orders.orderTotal", { total: money(view.total) })}</p>
      </Section>

      <Section title={t("admin.orders.payments")}>
        {view.schedule.map((item) => {
          const transfers = view.transfers.filter((transfer) => transfer.scheduleItemId === item.id);
          return (
            <div key={item.id} className="flex flex-col gap-1">
              <p className="font-medium">
                {`${t(`trade.itemType.${item.type}`)} · ${money(item.amount)} · ${t(`trade.itemStatus.${item.status}`)}`}
                {item.paidAt !== null &&
                  ` · ${t("admin.orders.paidAt", { when: formatDateTime(item.paidAt) })}`}
              </p>
              {transfers.length > 0 && (
                <Rows
                  head={[
                    t("admin.orders.reference"),
                    t("admin.orders.amount"),
                    t("admin.orders.paidOn"),
                    t("admin.orders.transferStatus"),
                  ]}
                  rows={transfers.map((transfer) => [
                    transfer.bankReference,
                    money(transfer.amount),
                    transfer.paidOn,
                    transfer.rejectReason === null
                      ? t(`admin.orders.transferStates.${transfer.status}`)
                      : t("admin.orders.rejectedBecause", { reason: transfer.rejectReason }),
                  ])}
                />
              )}
            </div>
          );
        })}
      </Section>

      <Section title={t("admin.orders.shipments")}>
        {view.shipments.length === 0 ? (
          none
        ) : (
          <Rows
            head={[
              t("admin.orders.carrier"),
              t("admin.orders.tracking"),
              t("admin.orders.shipmentStatus"),
              t("admin.orders.shippedAt"),
            ]}
            rows={view.shipments.map((shipment) => [
              shipment.carrier,
              shipment.trackingNumber ?? "",
              t(`trade.shipments.status.${shipment.status}`),
              formatDateTime(shipment.shippedAt),
            ])}
          />
        )}
      </Section>

      <Section title={t("admin.orders.changes")}>
        {view.cancellation === null && view.changeOrders.length === 0 && none}
        {view.cancellation !== null && (
          <p>
            {t("admin.orders.cancellation", {
              side: view.cancellation.requestedBySide === "buyer" ? buyer : shop,
              status: t(`admin.orders.requestStates.${view.cancellation.status}`),
              reason: view.cancellation.reason,
            })}
          </p>
        )}
        {view.changeOrders.map((change) => (
          <p key={change.id}>
            {t("admin.orders.changeOrder", {
              side: change.proposedBySide === "buyer" ? buyer : shop,
              status: t(`admin.orders.changeStates.${change.status}`),
              when: formatDateTime(change.createdAt),
            })}
          </p>
        ))}
      </Section>

      <Section title={t("admin.orders.documents")}>
        {view.documents.length === 0
          ? none
          : view.documents.map((document) => (
              <p key={document.id}>{`${document.number} · ${formatDateTime(document.issuedAt)}`}</p>
            ))}
      </Section>

      <Section title={t("admin.orders.ledger")}>
        {view.ledger.length === 0
          ? none
          : view.ledger.map((journal) => (
              <div key={journal.id} className="flex flex-col gap-1">
                <p className="font-medium">
                  {`${t(`admin.orders.journals.${journal.kind}`)} · ${formatDateTime(journal.occurredAt)}`}
                  {journal.externalReference !== null && ` · ${journal.externalReference}`}
                </p>
                <Rows
                  head={[
                    t("admin.orders.account"),
                    t("admin.orders.of"),
                    t("admin.orders.debit"),
                    t("admin.orders.credit"),
                  ]}
                  rows={journal.entries.map((entry) => [
                    t(`admin.orders.accounts.${entry.account}`),
                    partyOf(entry.enterpriseId),
                    entry.direction === "debit" ? money(entry.amount) : "",
                    entry.direction === "credit" ? money(entry.amount) : "",
                  ])}
                />
              </div>
            ))}
      </Section>

      <Section title={t("admin.orders.conversation")}>
        {view.conversation === null ? (
          none
        ) : (
          <Rows
            head={[
              t("admin.orders.person"),
              t("admin.orders.of"),
              t("admin.orders.role"),
              t("admin.orders.since"),
            ]}
            rows={view.conversation.participants.map((person) => [
              person.displayName,
              partyOf(person.enterpriseId),
              t(`admin.orders.roles.${person.role}`),
              person.leftAt === null
                ? formatDateTime(person.joinedAt)
                : t("admin.orders.left", { when: formatDateTime(person.leftAt) }),
            ])}
          />
        )}
        <p className="text-ink-muted">{t("admin.orders.noMessages")}</p>
        {view.conversation !== null && readsContent && (
          <Link
            to={`/conversations/${view.conversation.id}/messages?orderId=${view.id}`}
            className="self-start font-medium text-brand-600 hover:text-brand-700"
          >
            {t("admin.content.read")}
          </Link>
        )}
      </Section>
      {moderates && <ReviewSection orderId={view.id} />}
    </section>
  );
}

type OpsReview = Schemas["ReviewView"];
type HidingCategory = Schemas["ReviewHidingCategory"];
const HIDING_CATEGORIES: readonly HidingCategory[] = [
  "abuse",
  "personal_data",
  "unrelated",
  "legal_requirement",
  "online_harm",
];

/**
 * The order's review, for operators who keep the content rules (card 092): Hide, with a category and a reason both
 * sides read; for a legal requirement, the law may forbid saying why, and both sides read a generic reason (card 126).
 */
function ReviewSection({ orderId }: { orderId: string }) {
  const t = useTranslations();
  const { session } = useSession();
  const [review, setReview] = useState<Omit<OpsReview, "side" | "actions"> | null | undefined>();
  const [category, setCategory] = useState<HidingCategory>("abuse");
  const [reason, setReason] = useState("");
  const [withhold, setWithhold] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    void session.api
      .GET("/v1/ops/orders/{orderId}/review", { params: { path: { orderId } } })
      .then(({ data }) => setReview(data ?? null));
  }, [session, orderId]);

  const hide = async (event: FormEvent) => {
    event.preventDefault();
    if (review == null) return;
    if (reason.trim().length < 10) return setError(t("admin.orders.review.reasonTooShort"));
    const { data, error: problem } = await session.api.POST("/v1/ops/reviews/{reviewId}/hide", {
      params: { path: { reviewId: review.id } },
      body: {
        category,
        reason: reason.trim(),
        withholdReason: category === "legal_requirement" && withhold,
      },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setError(undefined);
    setReview(data);
  };

  if (review === undefined) return null;
  return (
    <Section title={t("admin.orders.review.title")}>
      {review === null ? (
        <p className="text-ink-muted">{t("admin.orders.review.none")}</p>
      ) : (
        <>
          <p>
            {t("admin.orders.review.ratings", {
              quality: review.ratings.quality,
              communication: review.ratings.communication,
              delivery: review.ratings.delivery,
            })}
          </p>
          {review.text !== null && <p className="whitespace-pre-wrap">{review.text}</p>}
          {review.reply !== null && (
            <p className="whitespace-pre-wrap text-ink-muted">
              {t("admin.orders.review.reply", { reply: review.reply })}
            </p>
          )}
          {review.status === "hidden" ? (
            <p className="text-ink-muted">
              {review.hiddenCategory === null
                ? t("admin.orders.review.hiddenBefore", { reason: review.hiddenReason ?? "" })
                : t("admin.orders.review.hidden", {
                    category: t(`admin.orders.review.categories.${review.hiddenCategory}`),
                    reason: review.hiddenReason ?? "",
                  })}
              {review.hiddenReasonWithheld && <> {t("admin.orders.review.hiddenWithheld")}</>}
            </p>
          ) : (
            <form className="flex flex-col gap-2" onSubmit={(event) => void hide(event)}>
              <label htmlFor="review-hide-category" className="font-medium">
                {t("admin.orders.review.category")}
              </label>
              <Select
                id="review-hide-category"
                value={category}
                onChange={(event) => setCategory(event.target.value as HidingCategory)}
              >
                {HIDING_CATEGORIES.map((each) => (
                  <option key={each} value={each}>
                    {t(`admin.orders.review.categories.${each}`)}
                  </option>
                ))}
              </Select>
              <label htmlFor="review-hide-reason" className="font-medium">
                {t("admin.orders.review.reason")}
              </label>
              <Input
                id="review-hide-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              {category === "legal_requirement" && (
                <label className="flex cursor-pointer items-center gap-2 text-ink">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-600"
                    checked={withhold}
                    onChange={(event) => setWithhold(event.target.checked)}
                  />
                  {t("admin.orders.review.withhold")}
                </label>
              )}
              {error !== undefined && <ErrorText>{error}</ErrorText>}
              <Button type="submit" size="sm" variant="secondary" className="self-start">
                {t("admin.orders.review.hide")}
              </Button>
            </form>
          )}
        </>
      )}
    </Section>
  );
}
